import {
    type ApiContainerAction,
    type ApiContainerLine,
    type ApiOrderPosition,
    EnumContainerOrderAction,
    EnumProcessState,
    isBooked,
} from '@Model';
import { type PlannedContainerGroup } from './containerActionGroups';

/** What the rows of the container card read from the order around them. */
export interface ContainerOrderState {
    completedAt: string | undefined;
    positions: ApiOrderPosition[];
    /** The number of every row when editing began or, for a row added later, when it first appeared, by row id. */
    numbersAtEditStart: ReadonlyMap<string, string>;
    /** The rows that name nothing standing or planned at the order's site, by id. */
    foreignToSite: ReadonlySet<string>;
    /** Whether the completed order is being corrected, which opens the service of booked rows. */
    correcting: boolean;
}

/**
 * Whether the row's number may be picked: a placement always, a row from the stock once the order is completed
 * or while it has no number, and also after a pick if it had none at first.
 */
// @ai-generated 2026-10-30T09:13:36+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5
export function canPickNumber(
    action: ApiContainerAction,
    completedAt: string | undefined,
    numbersAtEditStart: ReadonlyMap<string, string>,
): boolean {
    if (action.action === EnumContainerOrderAction.Place || completedAt || !action.container.foreign_key) {
        return true;
    }

    return numbersAtEditStart.has(action.id) && !numbersAtEditStart.get(action.id);
}

/** Whether a position of the material the row carries already holds a weighing slip. */
// @ai-generated 2026-10-15T12:08:29+00:00 by malte.ulfers@tngtech.com using Claude Opus 5.5 (1M context)
export function isContentWeighed(action: ApiContainerAction, positions: readonly ApiOrderPosition[]): boolean {
    const material = action.content.foreign_key;

    return (
        action.action !== EnumContainerOrderAction.Place &&
        !!material &&
        positions.some((position) => position.service.foreign_key === material && position.weighing_slips.length > 0)
    );
}

/**
 * The number the container action had when editing started, if it has another one now. Not for booked ones and not
 * while a completed order is corrected, because the correction box names those changes.
 */
// @ai-generated 2026-10-15T12:08:29+00:00 by malte.ulfers@tngtech.com using Claude Opus 5.5 (1M context)
export function plannedNumberOf(
    action: ApiContainerAction,
    numbersAtEditStart: ReadonlyMap<string, string>,
    correcting = false,
): string | undefined {
    if (correcting || isBooked(action.booking_status)) {
        return undefined;
    }

    const planned = numbersAtEditStart.get(action.id);

    return planned && planned !== action.container.display_string ? planned : undefined;
}

/** The number of every row, by row id. */
// @ai-generated 2026-10-15T12:08:29+00:00 by malte.ulfers@tngtech.com using Claude Opus 5.5 (1M context)
export function numbersOf(actions: readonly ApiContainerAction[]): ReadonlyMap<string, string> {
    return new Map(actions.map((action) => [action.id, action.container.display_string]));
}

/** Whether an open pickup or emptying names nothing that stands or is planned at the site. */
// @ai-generated 2026-10-15T12:08:29+00:00 by malte.ulfers@tngtech.com using Claude Opus 5.5 (1M context)
export function isForeignToSite(
    action: ApiContainerAction,
    standing: readonly ApiContainerLine[],
    planned: readonly ApiContainerLine[],
): boolean {
    if (action.action === EnumContainerOrderAction.Place || isBooked(action.booking_status)) {
        return false;
    }

    const lines = [...standing, ...planned];
    const containerId = action.container.foreign_key;

    return containerId
        ? !lines.some((line) => line.container.foreign_key === containerId)
        : !lines.some((line) => line.container_type.foreign_key === action.container_type.foreign_key);
}

/** How far a pooled pickup or emptying may grow: the stock less other open rows of the type, plus its booked rows. */
// @ai-generated 2026-10-15T12:08:29+00:00 by malte.ulfers@tngtech.com using Claude Opus 5.5 (1M context)
/** The key the stock at a site is counted by: a container type and a content. */
export const stockCountKey = (typeId: string | undefined, contentId: string | undefined) =>
    `${typeId ?? ''}|${contentId ?? ''}`;

/** {@link poolCap} with the stock of the group's type and content looked up in counts by {@link stockCountKey}. */
export const poolCapAtSite = (
    group: PlannedContainerGroup,
    actions: readonly ApiContainerAction[],
    standingCount: ReadonlyMap<string, number>,
) => poolCap(group, actions, standingCount.get(stockCountKey(group.id, group.actions[0]?.content.foreign_key)));

export function poolCap(
    group: PlannedContainerGroup,
    actions: readonly ApiContainerAction[],
    standing: number | undefined,
): number | undefined {
    if (group.action === EnumContainerOrderAction.Place || standing === undefined) {
        return undefined;
    }

    const takenElsewhere = actions.filter(
        (action) =>
            action.action !== EnumContainerOrderAction.Place &&
            !isBooked(action.booking_status) &&
            action.container_type.foreign_key === group.id &&
            action.content.foreign_key === group.actions[0]?.content.foreign_key &&
            !group.actions.includes(action),
    ).length;

    const bookedHere = group.actions.filter((action) => isBooked(action.booking_status)).length;

    return Math.max(0, standing - takenElsewhere) + bookedHere;
}

/** Whether the order's containers may still be changed: up to the release, so also while it is reported back. */
// @ai-generated 2026-10-15T12:08:29+00:00 by malte.ulfers@tngtech.com using Claude Opus 5.5 (1M context)
export function acceptsContainerChanges(processState: EnumProcessState): boolean {
    return processState !== EnumProcessState.Approved && processState !== EnumProcessState.Completed;
}

/**
 * Whether new container actions may be planned, from the card or the sidebar: while the order is open, and
 * on a completed order only during a correction.
 */
// @ai-generated 2026-10-15T12:08:29+00:00 by malte.ulfers@tngtech.com using Claude Opus 5.5 (1M context)
export function acceptsNewContainerActions(processState: EnumProcessState, editing: boolean): boolean {
    if (processState === EnumProcessState.ExecutionFinished) {
        return editing;
    }

    return processState === EnumProcessState.Open || processState === EnumProcessState.InProgress;
}
